import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select'
import { Product } from '@/interfaces/catalog/Product'
import { Sku } from '@/interfaces/catalog/Sku'
import { formatPrice } from '@/lib/formats/formatPrice'

import { skuTaxRate, skuUnitPrice } from './registerUtils'

interface VariantAxisPickerProps {
	product: Product
	skus: Sku[]
	onSkuResolved: (sku: Sku) => void
	onBack: () => void
	getAvailableToAdd: (skuId: string) => number
}

function findMatchingSku<T extends Pick<Sku, 'optionValueIds'>>(
	skus: T[],
	selectedValueIds: string[],
): T | undefined {
	if (selectedValueIds.length === 0) {
		return undefined
	}

	const selected = new Set(selectedValueIds)

	return skus.find((sku) => {
		const optionIds = sku.optionValueIds ?? []
		if (optionIds.length !== selected.size) {
			return false
		}
		return optionIds.every((id) => selected.has(id))
	})
}

export function VariantAxisPicker({
	product,
	skus,
	onSkuResolved,
	onBack,
	getAvailableToAdd,
}: VariantAxisPickerProps) {
	const { t } = useTranslation()
	const axes = [...(product.variantAxes ?? [])].sort(
		(a, b) => a.sortOrder - b.sortOrder,
	)

	const [selections, setSelections] = useState<Record<string, string>>({})

	const selectedValueIds = useMemo(
		() => axes.map((a) => selections[a.axisId]).filter(Boolean),
		[axes, selections],
	)

	const allSelected = axes.length > 0 && selectedValueIds.length === axes.length
	const matchedSku = allSelected
		? findMatchingSku(skus, selectedValueIds)
		: undefined
	const isOutOfStock =
		matchedSku != null && getAvailableToAdd(matchedSku.id) <= 0

	// Disable values that cannot form a valid in-stock combo given current partial selection
	const isValueAvailable = (axisId: string, valueId: string): boolean => {
		const next = { ...selections, [axisId]: valueId }
		const partialIds = axes
			.map((a) => next[a.axisId])
			.filter(Boolean)

		return skus.some((sku) => {
			const optionIds = sku.optionValueIds ?? []
			if (optionIds.length === 0) {
				return false
			}
			const optionSet = new Set(optionIds)
			if (!partialIds.every((id) => optionSet.has(id))) {
				return false
			}
			return getAvailableToAdd(sku.id) > 0
		})
	}

	return (
		<div className="flex flex-col h-full gap-4">
			<div className="flex items-center gap-2">
				<Button variant="outline" size="sm" onClick={onBack}>
					{t('cashRegister.calc.productsModal.back')}
				</Button>
				<h3 className="text-sm font-semibold text-gray-700">{product.name}</h3>
			</div>

			<div className="space-y-3 flex-1 overflow-auto">
				{axes.map((assignment) => (
					<div key={assignment.axisId} className="space-y-1">
						<label className="text-sm font-medium">
							{assignment.axis.name}
						</label>
						<Select
							value={selections[assignment.axisId] ?? ''}
							onValueChange={(value) =>
								setSelections((prev) => ({
									...prev,
									[assignment.axisId]: value,
								}))
							}
						>
							<SelectTrigger>
								<SelectValue
									placeholder={t('cashRegister.calc.productsModal.selectVariant')}
								/>
							</SelectTrigger>
							<SelectContent>
								{(assignment.axis.values ?? []).map((value) => {
									const disabled = !isValueAvailable(
										assignment.axisId,
										value.id,
									)
									return (
										<SelectItem
											key={value.id}
											value={value.id}
											disabled={disabled}
										>
											{value.label}
											{disabled ? ` (${t('cashRegister.calc.productsModal.outOfStock')})` : ''}
										</SelectItem>
									)
								})}
							</SelectContent>
						</Select>
					</div>
				))}
			</div>

			{allSelected && matchedSku && (
				<div className="border-t pt-3 space-y-2">
					<div className="flex justify-between text-sm">
						<span>{matchedSku.name}</span>
						{matchedSku.price?.price?.amount &&
							formatPrice(skuUnitPrice(matchedSku), {
								currency: matchedSku.price.price.currency,
							})}
					</div>
					<Button
						className="w-full"
						disabled={isOutOfStock}
						onClick={() => onSkuResolved(matchedSku)}
					>
						{isOutOfStock
							? t('cashRegister.calc.productsModal.outOfStock')
							: t('cashRegister.calc.productsModal.add')}
					</Button>
				</div>
			)}

			{allSelected && !matchedSku && (
				<p className="text-sm text-muted-foreground">
					{t('cashRegister.calc.productsModal.outOfStock')}
				</p>
			)}
		</div>
	)
}

export { findMatchingSku }
